import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { APP_THEME_STORAGE_KEY } from '../theme/tokens';
import { DEFAULT_THEME_MODE, getStoredThemeMode, getSystemMode, normalizeThemeMode } from '../theme/mode';

const ThemeModeContext = createContext(null);

export function ThemeModeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(getStoredThemeMode);
  const [systemMode, setSystemMode] = useState(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = event => setSystemMode(event.matches ? 'dark' : 'light');

    handleChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const resolvedMode = themeMode === 'system' ? systemMode : themeMode;

  useEffect(() => {
    try {
      localStorage.setItem(APP_THEME_STORAGE_KEY, themeMode);
    } catch (error) {
      // localStorage may be unavailable; ignore gracefully.
    }
  }, [themeMode]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.dataset.themePreference = themeMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode, themeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      resolvedMode,
      systemMode,
      updateThemeMode: mode => setThemeMode(normalizeThemeMode(mode) || themeMode || DEFAULT_THEME_MODE),
    }),
    [themeMode, resolvedMode, systemMode]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }

  return context;
}
